import { App } from '@capacitor/app';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';

import { getBackendUrl } from '@/config/backendUrl';
import { signOutOnLostSession } from '../../../../services/chatStream/chatStreamDeps';
import { capture } from '@/utils/analytics';
import { claimMediaFocus, registerMediaFocusOwner } from '@/utils/mediaFocus';
import { speak, stopSpeaking } from './chatSpeech';

export type ChatAudioState = 'idle' | 'loading' | 'playing' | 'error';

interface AudioManifest {
  messageId: string;
  voice: string;
  model: string;
  parts: number;
  chars: number;
  truncated: boolean;
}

interface StartOptions {
  messageId: string;
  text: string;
  /** False for a locally-created answer that has no server audio route yet. */
  useServerAudio?: boolean;
  onStateChange?: (state: ChatAudioState) => void;
  onNotice?: (notice: string) => void;
  onDone?: () => void;
}

interface ChatAudioDeps {
  audio: HTMLAudioElement;
  /**
   * Resolved per request, not once at construction: the default engine is
   * memoized for the lifetime of the tab, so a frozen value would keep an
   * ORA-573 backend override from ever reaching audio synthesis.
   */
  getBaseUrl: () => string;
  fetchImpl: typeof fetch;
  /** The backend no longer accepts the session cookie (a 401). */
  onUnauthorized?: () => void;
  speakFallback: typeof speak;
  stopFallback: typeof stopSpeaking;
  capture: typeof capture;
}

interface ActivePlayback extends StartOptions {
  id: number;
  controller: AbortController;
  entry: CachedMessage;
  manifest?: AudioManifest;
  part: number;
  partPlaying: boolean;
  fallback: boolean;
  prefetched?: Promise<Blob>;
}

interface RequestOwner {
  sessionId: number;
  cancelled?: boolean;
}

interface PendingRequest<T> {
  owner: RequestOwner;
  promise: Promise<T>;
}

interface CachedMessage {
  manifest?: PendingRequest<AudioManifest>;
  parts: Map<number, Blob>;
  pendingParts: Map<number, PendingRequest<Blob>>;
  resumePart?: number;
}

type AudioErrorCode =
  | 'not_found'
  | 'no_speakable_text'
  | 'daily_limit'
  | 'ai_budget'
  | 'tts_unavailable';

class ChatAudioRequestError extends Error {
  constructor(
    readonly code?: AudioErrorCode,
    readonly resetAt?: string
  ) {
    super(code ?? 'network');
  }
}

/** A short valid silent WAV. Calling play in the tap handler unlocks one
 * reusable media element before any network await (notably on iOS Safari). */
const SILENT_AUDIO =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

const MESSAGE_CACHE_LIMIT = 30;

const fallbackNotice = (error: ChatAudioRequestError): string => {
  if (error.code === 'daily_limit') {
    const reset = error.resetAt
      ? ` The limit resets ${new Date(error.resetAt).toLocaleString()}.`
      : '';

    return `Daily high-quality audio limit reached.${reset} Reading with the basic voice.`;
  }
  if (error.code === 'ai_budget') {
    return 'High-quality audio is temporarily limited. Reading with the basic voice.';
  }
  if (error.code === 'no_speakable_text') {
    return 'High-quality audio is unavailable for this answer. Reading with the basic voice.';
  }
  if (error.code === 'not_found') {
    return 'This answer has no high-quality audio. Reading with the basic voice.';
  }
  return 'High-quality audio is unavailable. Reading with the basic voice.';
};

const readError = async (
  response: Response
): Promise<ChatAudioRequestError> => {
  try {
    const value = (await response.json()) as {
      error?: AudioErrorCode;
      resetAt?: string;
    };

    return new ChatAudioRequestError(value.error, value.resetAt);
  } catch {
    return new ChatAudioRequestError();
  }
};

export interface ChatAudioEngine {
  start(options: StartOptions): void;
  stop(messageId?: string): void;
  destroy(): void;
  isActive(messageId: string): boolean;
}

export const createChatAudioEngine = (deps: ChatAudioDeps): ChatAudioEngine => {
  const cache = new Map<string, CachedMessage>();
  let active: ActivePlayback | null = null;
  let sequence = 0;
  let objectUrl: string | null = null;
  let appListener: PluginListenerHandle | null = null;
  let destroyed = false;

  const setState = (session: ActivePlayback, state: ChatAudioState) => {
    if (active?.id === session.id) session.onStateChange?.(state);
  };

  const revokeUrl = () => {
    if (!objectUrl) return;
    URL.revokeObjectURL(objectUrl);
    objectUrl = null;
  };

  const stop = (messageId?: string, state: ChatAudioState = 'idle') => {
    if (!active || (messageId && active.messageId !== messageId)) return;
    const stopped = active;
    active = null;
    stopped.controller.abort();
    deps.audio.pause();
    deps.stopFallback();
    revokeUrl();
    stopped.onStateChange?.(state);
    stopped.onDone?.();

    deps.capture('chat_voice_playback_stopped', {
      messageId: stopped.messageId,
      surface: 'companion',
      fallback: stopped.fallback,
    });
  };

  const remember = (messageId: string): CachedMessage => {
    const existing = cache.get(messageId);
    if (existing) return existing;

    const entry = {
      parts: new Map<number, Blob>(),
      pendingParts: new Map<number, PendingRequest<Blob>>(),
    };

    cache.set(messageId, entry);
    while (cache.size > MESSAGE_CACHE_LIMIT) {
      const oldest = cache.keys().next().value as string | undefined;
      if (!oldest) break;
      cache.delete(oldest);
    }
    return entry;
  };

  const request = async (
    session: ActivePlayback,
    owner: RequestOwner,
    suffix: string,
    accept: string
  ): Promise<Response> => {
    // Once fetch is sent, synthesis may already be charged: let it finish and
    // retain its result. A new playback takes ownership only of requests it
    // actually needs; a stopped later-part prefetch must not be revived by
    // replaying part zero.
    if (active?.id !== owner.sessionId || active.fallback) {
      owner.cancelled = true;
      throw new DOMException('Playback stopped', 'AbortError');
    }

    const response = await deps.fetchImpl(
      `${deps.getBaseUrl()}/api/v5/chat/messages/${encodeURIComponent(session.messageId)}/audio${suffix}`,
      {
        // The session cookie authenticates the request (ORA-987).
        credentials: 'include',
        headers: { Accept: accept },
      }
    );

    if (!response.ok) {
      // A dead session is not an audio problem: sign the tab out, as the
      // chat stream and axios do, before falling back (ORA-1004).
      if (response.status === 401) deps.onUnauthorized?.();
      throw await readError(response);
    }
    return response;
  };

  const getManifest = (session: ActivePlayback): Promise<AudioManifest> => {
    const entry = session.entry;
    if (entry.manifest && !entry.manifest.owner.cancelled) {
      entry.manifest.owner.sessionId = session.id;
      return entry.manifest.promise;
    }

    const owner = { sessionId: session.id };

    const pending: PendingRequest<AudioManifest> = {
      owner,
      promise: request(session, owner, '', 'application/json')
        .then(async (response) => {
          const manifest = (await response.json()) as AudioManifest;
          if (!Number.isInteger(manifest.parts) || manifest.parts < 1) {
            throw new ChatAudioRequestError('no_speakable_text');
          }
          return manifest;
        })
        .catch((error) => {
          if (entry.manifest === pending) entry.manifest = undefined;
          throw error;
        }),
    };

    entry.manifest = pending;
    return pending.promise;
  };

  const getPart = (session: ActivePlayback, part: number): Promise<Blob> => {
    const entry = session.entry;
    const cached = entry.parts.get(part);
    if (cached) return Promise.resolve(cached);
    const pending = entry.pendingParts.get(part);
    if (pending && !pending.owner.cancelled) {
      pending.owner.sessionId = session.id;
      return pending.promise;
    }

    const owner = { sessionId: session.id };

    const result: PendingRequest<Blob> = {
      owner,
      promise: request(session, owner, `/${part}`, 'audio/mpeg')
        .then((response) => response.blob())
        .then((blob) => {
          entry.parts.set(part, blob);
          return blob;
        })
        .finally(() => {
          if (entry.pendingParts.get(part) === result)
            entry.pendingParts.delete(part);
        }),
    };

    entry.pendingParts.set(part, result);
    return result.promise;
  };

  const finish = (session: ActivePlayback) => {
    if (active?.id !== session.id) return;
    active = null;
    session.entry.resumePart = undefined;
    revokeUrl();
    session.onStateChange?.('idle');
    session.onDone?.();
  };

  const fallback = (session: ActivePlayback, reason: unknown) => {
    if (active?.id !== session.id || session.controller.signal.aborted) return;

    // The manifest has no text boundaries. Reading the whole answer after
    // part zero would repeat finished content, so offer a retry at this part.
    if (session.manifest && session.part > 0) {
      session.entry.resumePart = session.part;
      session.onNotice?.('Playback stopped. Try again.');
      stop(session.messageId, 'error');
      return;
    }

    const error =
      reason instanceof ChatAudioRequestError
        ? reason
        : new ChatAudioRequestError();

    session.fallback = true;
    session.partPlaying = false;
    deps.audio.pause();
    revokeUrl();

    deps.capture('chat_voice_playback_fallback', {
      messageId: session.messageId,
      surface: 'companion',
      fallback: true,
      reason: error.code ?? 'network',
    });

    if (
      deps.speakFallback(session.text, () => {
        finish(session);
      })
    ) {
      session.onNotice?.(fallbackNotice(error));
      setState(session, 'playing');
    } else {
      active = null;
      session.onNotice?.('Audio is unavailable right now.');
      session.onStateChange?.('error');
      session.onDone?.();
    }
  };

  const playPart = async (session: ActivePlayback, blob: Blob) => {
    if (active?.id !== session.id || session.controller.signal.aborted) return;
    revokeUrl();
    objectUrl = URL.createObjectURL(blob);
    deps.audio.src = objectUrl;
    try {
      await deps.audio.play();
      if (active?.id !== session.id) return;

      session.partPlaying = true;
      setState(session, 'playing');
      const next = session.part + 1;
      if (next < (session.manifest?.parts ?? 0)) {
        session.prefetched = getPart(session, next);
        // Attach a rejection handler now: the current part may be long enough
        // that an already-rejected prefetch would otherwise sit unhandled.
        void session.prefetched.catch(() => undefined);
      }
    } catch (error) {
      fallback(session, error);
    }
  };

  const onEnded = () => {
    const session = active;
    if (!session?.manifest || !session.partPlaying || session.fallback) return;

    session.partPlaying = false;
    const next = session.part + 1;
    if (next >= session.manifest.parts) {
      finish(session);
      return;
    }
    session.part = next;
    const pending = session.prefetched ?? getPart(session, next);
    session.prefetched = undefined;

    void pending
      .then((blob) => playPart(session, blob))
      .catch((error) => {
        fallback(session, error);
      });
  };

  const onPause = () => {
    // A part that reaches its end is paused by the browser first: the spec
    // sets `paused`, fires `pause`, then fires `ended`. That pause is the hand
    // over to `onEnded`, not an interruption, or every answer would stop after
    // its first part (ORA-664).
    if (deps.audio.ended) return;

    // Any other browser pause can mean media interruption/backgrounding.
    // Programmatic pauses clear `active` first or immediately replace the
    // source.
    if (active?.manifest && active.partPlaying && !active.fallback) {
      stop(active.messageId);
    }
  };

  const onError = () => {
    const session = active;

    // Ignore failures from the throwaway unlock source. A real part owns an
    // object URL and has crossed the play() boundary before it can fail here.
    if (!session?.partPlaying || session.fallback || !objectUrl) return;
    fallback(session, new ChatAudioRequestError());
  };

  deps.audio.addEventListener('ended', onEnded);
  deps.audio.addEventListener('pause', onPause);
  deps.audio.addEventListener('error', onError);

  if (Capacitor.isNativePlatform()) {
    void App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) stop();
    })
      .then((listener) => {
        if (destroyed) void listener.remove();
        else appListener = listener;
      })
      .catch(() => undefined);
  }

  const start = (options: StartOptions) => {
    stop();
    deps.stopFallback();
    const entry = remember(options.messageId);

    const session: ActivePlayback = {
      ...options,
      id: ++sequence,
      controller: new AbortController(),
      entry,
      part: entry.resumePart ?? 0,
      partPlaying: false,
      fallback: false,
    };

    active = session;
    setState(session, 'loading');

    deps.capture('chat_voice_playback_started', {
      messageId: options.messageId,
      surface: 'companion',
      fallback: options.useServerAudio === false,
    });

    if (options.useServerAudio === false) {
      session.fallback = true;
      if (deps.speakFallback(session.text, () => finish(session))) {
        setState(session, 'playing');
      } else {
        active = null;
        session.onStateChange?.('error');
        session.onDone?.();
      }
      return;
    }

    // This call deliberately remains in the synchronous user-event stack.
    deps.audio.src = SILENT_AUDIO;
    void deps.audio.play().catch(() => undefined);

    void getManifest(session)
      .then(async (manifest) => {
        if (active?.id !== session.id) return;
        session.manifest = manifest;
        const blob = await getPart(session, session.part);
        await playPart(session, blob);
      })
      .catch((error) => fallback(session, error));
  };

  return {
    start,
    stop,
    isActive: (messageId) => active?.messageId === messageId,
    destroy: () => {
      destroyed = true;
      stop();
      deps.audio.removeEventListener('ended', onEnded);
      deps.audio.removeEventListener('pause', onPause);
      deps.audio.removeEventListener('error', onError);
      void appListener?.remove();
    },
  };
};

let defaultEngine: ChatAudioEngine | null = null;

const getDefaultEngine = (): ChatAudioEngine => {
  if (defaultEngine) return defaultEngine;
  const audio = new Audio();
  audio.preload = 'auto';

  defaultEngine = createChatAudioEngine({
    audio,
    getBaseUrl: getBackendUrl,
    fetchImpl: (...args) => fetch(...args),
    onUnauthorized: signOutOnLostSession,
    speakFallback: speak,
    stopFallback: stopSpeaking,
    capture,
  });

  return defaultEngine;
};

export const startChatAudio = (options: StartOptions): void => {
  // Reading an answer aloud pauses an Audio Overview episode (ORA-735).
  claimMediaFocus('chat-read-aloud');
  getDefaultEngine().start(options);
};

export const stopChatAudio = (messageId?: string): void =>
  defaultEngine?.stop(messageId);

registerMediaFocusOwner('chat-read-aloud', () => stopChatAudio());
